import { Link } from "react-router-dom";
import { AlertTriangle, Copy, Download, Eye, LayoutTemplate, Loader2, Share2, Trash2, PenSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { StatusSelect } from "@/components/common/StatusBadge";
import { assetUrl, formatDate, timeAgo } from "@/lib/api";
import SiteInfo from "./SiteInfo";

function Thumb({ p }) {
  if (p.thumbnail_url) return <img src={assetUrl(p.thumbnail_url)} alt="" className="w-full h-full object-cover object-top" />;
  const c = p.primary_color || "#334155";
  const initials = (p.company_name || p.name || "?").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <div className="w-full h-full flex items-end p-5" style={{ background: `linear-gradient(135deg, ${c} 0%, #0F172A 130%)` }}>
      <span className="text-4xl font-semibold text-white/90 tracking-tight">{initials}</span>
    </div>
  );
}

function IconAction({ label, onClick, icon: Icon, testId, danger }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon" onClick={onClick} aria-label={label} data-testid={testId}
          className={`h-10 w-10 shrink-0 sm:h-8 sm:w-8 ${danger ? "text-slate-400 hover:text-red-600 hover:bg-red-50" : "text-slate-500 hover:text-slate-900"}`}>
          <Icon size={16} />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export default function ProjectCard({ p, s, onStatus, onShare, onDuplicate, onSaveTemplate, onDelete, onExport, index = 0 }) {
  const running = p.generation?.status === "running";
  return (
    <article className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-sky-300 hover:shadow-[0_8px_30px_rgba(15,23,42,0.06)] transition-[border-color,box-shadow] fade-up"
      style={{ animationDelay: `${index * 40}ms` }} data-testid={`project-card-${p.id}`}>
      <Link to={`/projects/${p.id}`} className="block relative aspect-video bg-slate-100 overflow-hidden" data-testid={`project-thumb-${p.id}`}>
        <Thumb p={p} />
        {running && <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/95 text-sky-700 text-[11px] font-medium px-2.5 py-1 rounded-full"><Loader2 size={12} className="animate-spin" />Generazione AI in corso</span>}
        {p.generation?.status === "error" && !p.generation.dismissed && <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-white/95 text-red-600 text-[11px] font-medium px-2.5 py-1 rounded-full" data-testid={`project-generation-error-${p.id}`}><AlertTriangle size={12} />Generazione non completata</span>}
        {p.share?.enabled && <span className="absolute top-3 right-3 bg-navy-900/85 text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded">Pubblica</span>}
      </Link>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 truncate" data-testid="project-company">{p.company_name || "—"}</p>
            <h3 className="mt-1 font-semibold text-slate-900 truncate" data-testid="project-name">{p.name}</h3>
          </div>
          <StatusSelect status={p.status} onChange={(s) => onStatus(p, s)} testId={`project-status-${p.id}`} />
        </div>
        <p className="mt-3 text-xs text-slate-500">Creato {formatDate(p.created_at)} · Modificato {timeAgo(p.updated_at)}</p>
        <SiteInfo p={p} s={s} />
        <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center justify-center gap-x-2 gap-y-2" data-testid={`project-actions-${p.id}`}>
          <Button asChild size="sm" className="h-10 sm:h-9 bg-navy-900 hover:bg-navy-800 shrink-0 px-3" data-testid={`open-editor-btn-${p.id}`}>
            <Link to={`/projects/${p.id}`}><PenSquare size={14} className="mr-1.5" />Editor e pubblica</Link>
          </Button>
          <div className="flex items-center justify-center gap-0.5 min-w-0">
            <IconAction label="Anteprima" icon={Eye} testId={`preview-btn-${p.id}`} onClick={() => window.open(`/preview/${p.id}`, "_blank")} />
            <IconAction label="Condividi" icon={Share2} testId={`share-btn-${p.id}`} onClick={() => onShare(p)} />
            <IconAction label="Duplica…" icon={Copy} testId={`duplicate-btn-${p.id}`} onClick={() => onDuplicate(p)} />
            <IconAction label="Salva come template" icon={LayoutTemplate} testId={`save-template-btn-${p.id}`} onClick={() => onSaveTemplate(p)} />
            <IconAction label="Esporta (ZIP)" icon={Download} testId={`export-btn-${p.id}`} onClick={() => onExport(p)} />
            <IconAction label="Elimina" icon={Trash2} testId={`delete-btn-${p.id}`} onClick={() => onDelete(p)} danger />
          </div>
        </div>
      </div>
    </article>
  );
}
